import _jsx from '@babel/runtime/helpers/jsx';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import React, { cloneElement, useRef, useState, useEffect } from 'react';
import 'prop-types';
import classnames from 'classnames';
import { trackEvent } from '@vpp-frontend-components/common';
import { Icon } from '@vpp-frontend-components/icon';
import { ActionModal } from '@vpp-frontend-components/action-modal';
import { ActionRow } from '@vpp-frontend-components/action-row';
const namespace$4 = 'ui-pdp-collapsable';
const ActionCollapsableExpand = _ref => {
  let {
    action,
    className = '',
    collapsed,
    onClick = null
  } = _ref;
  const buttonContent = /*#__PURE__*/React.createElement(React.Fragment, null, action.label.text, /*#__PURE__*/_jsx(Icon, {
    id: "chevron",
    className: classnames(`${namespace$4}__chevron`, {
      'ui-pdp-icon--chevron-down': collapsed,
      'ui-pdp-icon--chevron-down--active': !collapsed
    })
  }));
  const button = action && (action.target ? /*#__PURE__*/_jsx("a", {
    "data-testid": "action-collapsable-target",
    className: classnames(`${namespace$4}__action`, className),
    title: action.label.text,
    onClick: onClick,
    href: action.target
  }, void 0, buttonContent) : /*#__PURE__*/_jsx("button", {
    className: classnames(`${namespace$4}__action`, className),
    type: "button",
    onClick: onClick,
    "data-testid": "action-collapsable-target"
  }, void 0, buttonContent));
  return button;
};
const namespace$3 = 'ui-pdp-collapsable';
const ActionCollapsableModal = _ref => {
  let {
    action,
    children,
    modalClassName = '',
    modalHash = null,
    modalTitle = null
  } = _ref;
  const WrappedComponent = /*#__PURE__*/cloneElement(children, {
    showTitle: false
  });
  const modalLocation = modalHash ? `#${modalHash}` : `#${action.target}`;
  return /*#__PURE__*/_jsx(ActionModal, {
    className: "ui-pdp-see-more__action",
    deviceType: "mobile",
    componentAsLabel: /*#__PURE__*/_jsx(ActionRow, {
      label: action.label.text,
      modifier: "secondary"
    }),
    modalClassName: classnames(`${namespace$3}__modal`, modalClassName),
    modalTitle: modalTitle,
    url: action.target,
    modalUrl: modalLocation,
    track: action.track,
    closeModalLabel: action.close_modal_label
  }, void 0, WrappedComponent);
};

/* constants */
const namespace$2 = 'ui-pdp-collapsable';
const MARGIN_BOX = 32;
const GAP = 10;

// eslint-disable-next-line complexity
const ActionCollapsable = _ref => {
  let {
    action,
    collapse = null,
    allowRecollapse = false,
    children = null,
    className = '',
    max = 1.5,
    maxCollapsed = 1,
    gradientType = 'default',
    collapseBy = 'pages',
    scrollToTop = true,
    onClickAction = null,
    showAction = true,
    withModal = false,
    modalHash = null,
    modalClassName = null,
    modalTitle = null,
    useCanSeeEverything = false,
    forceShowAction
  } = _ref;
  const ref = useRef();
  const [collapsedState, setCollapsedState] = useState({
    currentHeight: false,
    collapsedHeight: false,
    collapsed: false,
    viewportHeight: false,
    maxHeight: false,
    collapsable: false
  });
  const actionExpandCollapse = !collapsedState.collapsed && allowRecollapse ? collapse : action;
  const onClick = e => {
    e.preventDefault();
    setCollapsedState(_objectSpread(_objectSpread({}, collapsedState), {}, {
      collapsed: !collapsedState.collapsed,
      maxHeight: false
    }));
    if (scrollToTop) {
      ref.current.scrollIntoView();
    }
    trackEvent(actionExpandCollapse === null || actionExpandCollapse === void 0 ? void 0 : actionExpandCollapse.track);
    if (onClickAction) {
      onClickAction();
    }
  };
  useEffect(() => {
    if (!collapsedState.currentHeight) {
      // @TODO: revisar impacto en performance de este if
      const currentHeight = ref.current.offsetHeight;
      const viewportHeight = collapseBy === 'pages' ? window.innerHeight : 1;
      const maxHeight = Math.floor(viewportHeight * max);
      if (collapseBy === 'pages' && currentHeight > maxHeight * maxCollapsed || collapseBy === 'pixels' && currentHeight > maxCollapsed) {
        setCollapsedState({
          currentHeight,
          collapsed: true,
          collapsedHeight: viewportHeight * maxCollapsed,
          maxHeight,
          collapsable: true
        });
      }
    }
  }, [ref, collapsedState.currentHeight, collapsedState.maxHeight, max, maxCollapsed, collapseBy]);
  const {
    collapsed,
    collapsable,
    collapsedHeight,
    maxHeight,
    currentHeight
  } = collapsedState;
  const containerMaxHeight = collapsed ? collapsedHeight : maxHeight;
  const extendedHeight = currentHeight - MARGIN_BOX - GAP;
  const canSeeEverything = maxCollapsed >= extendedHeight; // content preview height === all content height
  const showButton = forceShowAction || (collapsable && allowRecollapse || collapsed) && action && action.label && (useCanSeeEverything ? !canSeeEverything : true);
  const isCollapsedClassNames = {
    [`${namespace$2}--is-collapsed`]: useCanSeeEverything ? collapsed && !canSeeEverything : collapsed,
    [`${namespace$2}--is-gradient-small`]: gradientType === 'small'
  };
  const seeMoreNoModal = showAction && showButton && !withModal;
  const seeMoreModal = showAction && showButton && withModal;
  const expandCollapseComponent = /*#__PURE__*/_jsx(ActionCollapsableExpand, {
    action: actionExpandCollapse,
    className: className,
    collapsed: collapsed,
    onClick: onClick
  });
  return /*#__PURE__*/React.createElement("div", {
    className: classnames(namespace$2, className, isCollapsedClassNames),
    ref: ref
  }, seeMoreNoModal && !collapsed && expandCollapseComponent, /*#__PURE__*/_jsx("div", {
    className: `${namespace$2}__container`,
    style: containerMaxHeight ? {
      maxHeight: `${containerMaxHeight}px`
    } : {}
  }, void 0, children), seeMoreNoModal && collapsed && expandCollapseComponent, seeMoreModal && /*#__PURE__*/_jsx(ActionCollapsableModal, {
    action: action,
    modalClassName: modalClassName,
    modalHash: modalHash,
    modalTitle: modalTitle
  }, void 0, children));
};
const ActionCollapsableButtonContent = _ref => {
  let {
    collapsed = false,
    namespace,
    text
  } = _ref;
  return /*#__PURE__*/React.createElement(React.Fragment, null, text, /*#__PURE__*/_jsx(Icon, {
    id: "chevron",
    className: classnames(`${namespace}__chevron ui-pdp-icon--chevron-down`, {
      'ui-pdp-icon--chevron-up--active': collapsed
    })
  }));
};
const ActionCollapsableButton = _ref => {
  let {
    action,
    collapsed = false,
    namespace = '',
    onClick
  } = _ref;
  return action.target ? /*#__PURE__*/_jsx("a", {
    className: `${namespace}__action`,
    title: action.label.text,
    onClick: onClick,
    href: action.target
  }, void 0, /*#__PURE__*/_jsx(ActionCollapsableButtonContent, {
    collapsed: collapsed,
    namespace: namespace,
    text: action.label.text
  })) : /*#__PURE__*/_jsx("span", {
    className: `${namespace}__action`,
    title: action.label.text,
    onClick: onClick,
    onKeyUp: onClick,
    role: "button",
    tabIndex: "-1"
  }, void 0, /*#__PURE__*/_jsx(ActionCollapsableButtonContent, {
    collapsed: collapsed,
    namespace: namespace,
    text: action.label.text
  }));
};
const namespace$1 = 'ui-pdp-collapsable';
const ActionCollapsableCss = _ref => {
  let {
    action,
    children = null,
    className = ''
  } = _ref;
  const ref = useRef();
  const [isCollapsed, setIsCollapsed] = useState(true);
  const onClick = e => {
    e.preventDefault();
    setIsCollapsed(!isCollapsed);
    ref.current.scrollIntoView();
  };
  return /*#__PURE__*/_jsx("div", {
    className: classnames(namespace$1, className, {
      [`${namespace$1}--is-collapsed`]: isCollapsed
    })
  }, void 0, /*#__PURE__*/React.createElement("div", {
    className: classnames(`${namespace$1}__container`, {
      [`${namespace$1}__container-items-hidden`]: isCollapsed
    }),
    ref: ref
  }, children), isCollapsed && /*#__PURE__*/_jsx(ActionCollapsableButton, {
    action: action,
    namespace: namespace$1,
    onClick: onClick
  }));
};
const namespace = 'ui-pdp-collapsable-mobile';
const ActionCollapsableCssMobile = _ref => {
  let {
    action,
    children = null,
    className = '',
    onSelect = null,
    scrollToTop = true
  } = _ref;
  const ref = useRef();
  const [isCollapsed, setIsCollapsed] = useState(true);
  const {
    track
  } = action;
  const onClick = e => {
    e.preventDefault();
    trackEvent(track);
    if (onSelect) {
      onSelect();
    }
    setIsCollapsed(!isCollapsed);
    if (scrollToTop) {
      ref.current.scrollIntoView();
    }
  };
  return /*#__PURE__*/_jsx("div", {
    className: classnames(namespace, className, {
      [`${namespace}--is-collapsed`]: isCollapsed
    })
  }, void 0, /*#__PURE__*/React.createElement("div", {
    className: classnames(`${namespace}__container`, {
      [`${namespace}__container-items-hidden`]: isCollapsed
    }),
    ref: ref
  }, children), isCollapsed && /*#__PURE__*/_jsx(ActionCollapsableButton, {
    action: action,
    namespace: namespace,
    onClick: onClick
  }));
};
export { ActionCollapsable, ActionCollapsableButton, ActionCollapsableCss, ActionCollapsableCssMobile, ActionCollapsableExpand, ActionCollapsableModal };
